import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const CheckIcon = () => (
    <svg className="w-5 h-5 mx-auto text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
);

const XIcon = () => (
    <svg className="w-5 h-5 mx-auto text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const CellValue = ({ value }) => {
    if (value === 'yes') return <CheckIcon />;
    if (value === 'no') return <XIcon />;
    return <span className="text-sm">{value}</span>;
};

export default function ComparisonTableSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'table';
    const stickyHeader = config.sticky_header !== false;

    const defaultColumns = [
        { label: 'Nosotros', highlighted: true },
        { label: 'Competencia A' },
        { label: 'Competencia B' },
    ];
    const defaultRows = [
        { feature: 'Usuarios', values: ['3', 'Ilimitados', 'Ilimitados'] },
        { feature: 'Almacenamiento', values: ['5 GB', '100 GB', '1 TB'] },
        { feature: 'Soporte', values: ['Email', 'Prioritario', '24/7 dedicado'] },
        { feature: 'API', values: ['no', 'yes', 'yes'] },
        { feature: 'SSO', values: ['no', 'no', 'yes'] },
    ];
    // Support both formats: columns+rows OR items (legacy/template format)
    let columns, rows;
    const hasColumnsRows = (content.columns || []).length > 0 || (content.rows || []).length > 0;
    const hasItems = (content.items || []).length > 0;
    if (hasColumnsRows) {
        columns = content.columns || defaultColumns;
        rows = content.rows || defaultRows;
    } else if (hasItems) {
        // Convert items format to columns+rows
        const items = content.items;
        columns = items.map(it => ({ label: it.name || it.title || '', highlighted: !!it.popular }));
        const allFeatures = [...new Set(items.flatMap(it => (it.features || '').split('\n').map(f => f.trim()).filter(Boolean)))];
        rows = allFeatures.map(feat => ({
            feature: feat,
            values: items.map(it => (it.features || '').split('\n').map(f => f.trim()).includes(feat) ? 'yes' : 'no'),
        }));
    } else {
        columns = defaultColumns;
        rows = defaultRows;
    }

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const borderColor = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
    const zebraEven = s.dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
    const highlightBg = hexToRgba(s.accent, 0.06);

    // Cards variant
    if (variant === 'cards') {
        const colCount = columns.length;
        const colsCls = colCount <= 2 ? 'md:grid-cols-2' : colCount === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4';

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                    <div className={`grid grid-cols-1 ${colsCls} gap-6`}>
                        {columns.map((col, ci) => (
                            <div key={ci}
                                className={`${card} p-6 transition-all duration-500 ${col.highlighted ? 'ring-2' : ''} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{
                                    transitionDelay: visible ? `${ci * 120}ms` : '0ms',
                                    ...(col.highlighted ? { ringColor: s.accent, borderColor: s.accent } : {}),
                                }}>
                                {col.highlighted && (
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-center py-1 -mt-6 -mx-6 mb-4 rounded-t-2xl text-white"
                                        style={{ backgroundColor: s.accent }}>
                                        {content.recommended_label || 'Recomendado'}
                                    </div>
                                )}
                                <h3 className="text-xl font-bold text-center mb-6" style={{ color: s.text }}>{col.label}</h3>
                                <div className="space-y-3">
                                    {rows.map((row, ri) => {
                                        const val = (row.values || [])[ci];
                                        return (
                                            <div key={ri} className="flex items-center gap-3 py-1.5" style={{ borderBottom: `1px solid ${borderColor}` }}>
                                                <div className="flex-1 text-sm" style={{ color: s.textSub }}>{row.feature}</div>
                                                <div className="shrink-0 w-8 text-center" style={{ color: s.text }}>
                                                    <CellValue value={val} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // Table variant (default)
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                        <table className="min-w-full text-sm" style={{ color: s.text }}>
                            <thead>
                                <tr className={stickyHeader ? 'sticky top-0 z-10' : ''}
                                    style={{ backgroundColor: s.bg || '#ffffff' }}>
                                    <th className="text-left py-4 px-4 font-semibold sticky left-0 z-10 min-w-[160px]"
                                        style={{
                                            color: s.textSub,
                                            backgroundColor: s.bg || '#ffffff',
                                            borderBottom: `2px solid ${borderColor}`,
                                        }}>
                                        {content.feature_label || 'Caracteristica'}
                                    </th>
                                    {columns.map((col, ci) => (
                                        <th key={ci}
                                            className="py-4 px-4 text-center font-bold min-w-[140px]"
                                            style={{
                                                color: col.highlighted ? s.accent : s.text,
                                                borderBottom: `2px solid ${col.highlighted ? s.accent : borderColor}`,
                                                borderTop: col.highlighted ? `3px solid ${s.accent}` : 'none',
                                                backgroundColor: col.highlighted ? highlightBg : 'transparent',
                                            }}>
                                            <div className="flex flex-col items-center gap-1">
                                                {col.highlighted && (
                                                    <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white mb-1"
                                                        style={{ backgroundColor: s.accent }}>
                                                        {content.recommended_label || 'Recomendado'}
                                                    </span>
                                                )}
                                                {col.label}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, ri) => (
                                    <tr key={ri}
                                        className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                                        style={{
                                            transitionDelay: visible ? `${ri * 50}ms` : '0ms',
                                            borderBottom: `1px solid ${borderColor}`,
                                        }}>
                                        <td className="py-3 px-4 font-medium sticky left-0 z-10"
                                            style={{
                                                color: s.text,
                                                backgroundColor: ri % 2 === 0 ? 'transparent' : zebraEven,
                                            }}>
                                            {row.feature}
                                        </td>
                                        {columns.map((col, ci) => {
                                            const val = (row.values || [])[ci];
                                            return (
                                                <td key={ci}
                                                    className="py-3 px-4 text-center"
                                                    style={{
                                                        color: s.text,
                                                        backgroundColor: col.highlighted
                                                            ? highlightBg
                                                            : (ri % 2 === 0 ? 'transparent' : zebraEven),
                                                    }}>
                                                    <CellValue value={val} />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
